import { RoundedBadge } from '@/components/rounded-badge';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { Quest } from '@/types';
import { getIconPathFromBadgeId } from '@/utils';
import { Card, HStack, Icon, Image, Text, VStack } from '@chakra-ui/react';
import { toWords } from 'number-to-words';
import { useEffect, useMemo, useState } from 'react';
import { IoCheckmark } from 'react-icons/io5';
import { useLocalStorage } from 'usehooks-ts';

export const QuestRewardCard = ({ quest }: { quest: Quest }) => {
  const questIsComplete = useMemo(() => quest?.tasks?.every((task) => task.completed), [quest]);
  const [loggedQuests, setLoggedQuests] = useLocalStorage<Record<string, boolean>>('loggedQuests', {});
  const badgeId = `quest_${toWords(quest.wave)}_badge`;
  const [badgeSrc, setBadgeSrc] = useState<string | undefined>(undefined);
  useEffect(() => {
    const src = getIconPathFromBadgeId(badgeId);
    let isCancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!isCancelled) setBadgeSrc(src);
    };
    img.onerror = () => {
      if (!isCancelled) setBadgeSrc(undefined);
    };
    img.src = src;
    return () => {
      isCancelled = true;
    };
  }, [badgeId]);

  useEffect(() => {
    // Check if we've logged this from local storage
    if (!loggedQuests[quest.id] && questIsComplete) {
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalCompletedQuest, {
        quest_name: quest.title,
      });
      setLoggedQuests({ ...loggedQuests, [quest.id]: true });
    }
  }, [questIsComplete, quest.id, loggedQuests, setLoggedQuests, quest.title]);

  const Badge = ({ boxSize }: { boxSize: number }) => {
    if (!badgeSrc) return null;
    return <Image src={badgeSrc} boxSize={boxSize} />;
  };

  const rewardCardDescription = useMemo(() => {
    if (questIsComplete) {
      if (badgeSrc) {
        return `You earned a badge and ${quest.xpReward} Plume Points`;
      }
      return `You earned ${quest.xpReward} Plume Points`;
    }
    if (badgeSrc) {
      return `Complete this quest to unlock a badge and earn ${quest.xpReward} Plume Points`;
    }
    return `Complete this quest to earn ${quest.xpReward} Plume Points`;
  }, [questIsComplete, quest.xpReward, badgeSrc]);

  return (
    <Card.Root
      bgColor={questIsComplete ? 'brandBlue' : 'bg.muted'}
      borderColor="border.muted"
      p={8}
      borderRadius="3xl"
      w="100%"
    >
      <Card.Body>
        <VStack alignItems={'center'} gap={4}>
          {questIsComplete ? (
            <Icon boxSize={10} color="blue.500">
              <IoCheckmark />
            </Icon>
          ) : (
            <Badge boxSize={10} />
          )}
          <VStack gap={questIsComplete ? 1 : 3}>
            <Card.Title fontSize="2xl" fontFamily="heading" fontWeight="bold" textAlign="center">
              {questIsComplete ? 'Quest Completed!' : 'A Reward Awaits You'}
            </Card.Title>
            <Card.Description fontSize="md" fontWeight="500" textAlign="center">
              {rewardCardDescription}
            </Card.Description>
          </VStack>
          {questIsComplete && (
            <HStack flexWrap="wrap" gap={3}>
              <RoundedBadge bgColor="teal.800" color="fg.inverted" py={2}>
                <Image src="/assets/pp-circle.svg" boxSize={7} />
                You've earned {quest.xpReward} Plume Points
              </RoundedBadge>
              {badgeSrc && (
                <RoundedBadge bgColor="brandBlue.800" color="fg.inverted" py={2}>
                  <Badge boxSize={7} />
                  New Badge Unlocked
                </RoundedBadge>
              )}
            </HStack>
          )}
          {questIsComplete && (
            <Text fontWeight="500" lineHeight="24px" color="text.secondary">
              Plume Points will be added to your account at 0:00 UTC
            </Text>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};
